import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { INotifySelectedParticipantsActionPayload } from 'app/library/module/interfaces/notfiy-selected.action.interface';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { Moment } from 'moment';
import {
    AddCertificateRecord,
    AddCertificateRecordFailure,
    AddCertificateRecordSuccess,
    AssignCertificateToModuleUser,
    AssignCertificateToModuleUserFailure,
    AssignCertificateToModuleUsers,
    AssignCertificateToModuleUsersFailure,
    AssignCertificateToModuleUsersSuccess,
    AssignCertificateToModuleUserSuccess,
    FetchCertificateRecord,
    FetchCertificateRecordFailure,
    FetchCertificateRecordSuccess,
    FetchUserCourseCertificates,
    FetchUserCourseCertificatesSuccess,
    UpdateCertificateRecord,
    UpdateCertificateRecordFailure,
    UpdateCertificateRecordSuccess,
} from '../actions';
import { CertificateRecord } from '../models/certificate-record';
import { CertificateRecordState } from '../models/certificate-record.state';
import { CertificateRecordApiService } from './certificate-record.api-service';

@Injectable({
    providedIn: 'root',
})
export class CertificateRecordService {
    constructor(private _store: Store, private _actionHelper: NgxsActionHelper, private _certificateRecordApiService: CertificateRecordApiService) {}

    getCertificateRecord(): CertificateRecord {
        return this._store.selectSnapshot(CertificateRecordState.getEntity);
    }

    async fetchCertificateRecord(id: string): Promise<boolean> {
        this._store.dispatch(new FetchCertificateRecord(id));
        return await this._actionHelper.race(FetchCertificateRecordSuccess, FetchCertificateRecordFailure).toPromise();
    }

    async addCertificateRecord(
        certificationId: string,
        issueDate: Moment,
        expirationDate: Moment,
        sendNotification: boolean,
        userId: string,
        file: File,
        notifyAboutExpiration: boolean
    ): Promise<boolean> {
        this._store.dispatch(new AddCertificateRecord({ certificationId, issueDate, expirationDate, sendNotification, userId, file, notifyAboutExpiration }));

        return await this._actionHelper.race(AddCertificateRecordSuccess, AddCertificateRecordFailure).toPromise();
    }

    async assignCertificateToModuleUser(
        participantId: string,
        certificationId: string,
        issueDate: Moment,
        expirationDate: Moment,
        sendNotification: boolean,
        file: File
    ): Promise<boolean> {
        this._store.dispatch(new AssignCertificateToModuleUser({ participantId, certificationId, issueDate, expirationDate, sendNotification, file }));

        return await this._actionHelper.race(AssignCertificateToModuleUserSuccess, AssignCertificateToModuleUserFailure).toPromise();
    }

    async assignCertificateToModuleUsers(moduleUserIds: string[], moduleCertificationIds: string[], sendNotification: boolean): Promise<boolean> {
        this._store.dispatch(new AssignCertificateToModuleUsers({ moduleUserIds, moduleCertificationIds, sendNotification }));

        return await this._actionHelper.race(AssignCertificateToModuleUsersSuccess, AssignCertificateToModuleUsersFailure).toPromise();
    }

    async updateCertificateRecord(
        certificateRecordId: string,
        certificationId: string,
        issueDate: Moment,
        expirationDate: Moment,
        notifyAboutExpiration: boolean
    ): Promise<boolean> {
        this._store.dispatch(new UpdateCertificateRecord({ certificateRecordId, certificationId, issueDate, expirationDate, notifyAboutExpiration }));

        return await this._actionHelper.race(UpdateCertificateRecordSuccess, UpdateCertificateRecordFailure).toPromise();
    }

    async downloadCertificate(certificateId: string): Promise<void> {
        await this._certificateRecordApiService.downloadCertificate(certificateId).toPromise();
    }

    async getCertificatesToReplace(userId: string, certificationId: string, expirationDate: string): Promise<CertificateRecord[]> {
        return await this._certificateRecordApiService.getCertificatesToReplace(userId, certificationId, expirationDate).toPromise();
    }

    async getUserCourseCertificates(pageSize = 10, pageNumber = 1, search = '', userId: string): Promise<boolean> {
        this._store.dispatch(new FetchUserCourseCertificates({ userId, pageSize, pageNumber, search }));

        return await this._actionHelper.race(FetchUserCourseCertificatesSuccess, FetchUserCourseCertificates).toPromise();
    }

    async notifySelected(payload: INotifySelectedParticipantsActionPayload): Promise<any> {
        return await this._certificateRecordApiService.notifySelected(payload).toPromise();
    }

    async teamsNotifySelected(teamId: string, payload: INotifySelectedParticipantsActionPayload): Promise<any> {
        return await this._certificateRecordApiService.teamsNotifySelected(teamId, payload).toPromise();
    }

    async fetchNotificationKeywords(): Promise<string[]> {
        return await this._certificateRecordApiService.fetchNotificationKeywords().toPromise();
    }
}
